import { Handle, NodeToolbar, Position, useNodeConnections, useNodeId } from '@xyflow/react'
import { ArrowsOutCardinalIcon, CopyIcon, TrashIcon, WarningIcon, type Icon } from '@phosphor-icons/react'
import { Button } from 'app/components/ui/button'
import { cn } from 'app/lib/utils'
import { runTone, useRunStatus } from '../runViz'
import { useWorkflowActions } from '../WorkflowActions'
import { AppendButton } from './AppendButton'

const RUN_TONE: Record<string, { ring: string; chip: string; text: string; label: string; pulse?: boolean }> = {
  running: { ring: `ring-blue-400`, chip: `bg-blue-500`, text: `text-on-color`, label: `Running`, pulse: true },
  waiting: { ring: `ring-amber-400`, chip: `bg-amber-500`, text: `text-on-color`, label: `Waiting` },
  done: { ring: `ring-emerald-500`, chip: `bg-emerald-500`, text: `text-on-color`, label: `Done` },
  failed: { ring: `ring-red-400`, chip: `bg-red-500`, text: `text-on-color`, label: `Failed` },
  exited: { ring: `ring-strong`, chip: `bg-(--fg-minimal)`, text: `text-on-color`, label: `Exited` },
}

type Accent =
  | `emerald`
  | `amber`
  | `blue`
  | `rose`
  | `violet`
  | `indigo`
  | `teal`
  | `orange`
  | `sky`
  | `cyan`
  | `slate`
  | `pink`

type NodeShellProps = {
  icon: Icon
  accent: Accent
  kind: string
  label: string
  description?: string
  selected?: boolean
  hasTarget?: boolean
  hasSource?: boolean
  /** False for a step with no outlet (Exit): there is nowhere below it for a copy to go. */
  duplicable?: boolean
  /** A CSS width for a card whose exits need more room than the standard one (Wait for a keyword). */
  width?: string
  /** Extra content rendered inside the card (e.g. custom branch handles). */
  children?: React.ReactNode
}

// Grayscale palette — every block uses the same neutral chip; the icon carries the meaning.
const GRAY_ACCENT = { chip: `bg-surface-muted text-subtle`, ring: `ring-(--fg-minimal)` }
const ACCENT: Record<Accent, { chip: string; ring: string }> = {
  emerald: GRAY_ACCENT,
  amber: GRAY_ACCENT,
  blue: GRAY_ACCENT,
  rose: GRAY_ACCENT,
  violet: GRAY_ACCENT,
  indigo: GRAY_ACCENT,
  teal: GRAY_ACCENT,
  orange: GRAY_ACCENT,
  sky: GRAY_ACCENT,
  cyan: GRAY_ACCENT,
  slate: GRAY_ACCENT,
  pink: GRAY_ACCENT,
}

const HANDLE_CLASS = `!h-2.5 !w-2.5 !border-2 !border-(--surface) !bg-inverse/40`

/** Shared visual shell for every workflow node, with optional top/bottom connection handles. */
export function NodeShell({
  icon: IconCmp,
  accent,
  kind,
  label,
  description,
  selected,
  hasTarget = true,
  hasSource = true,
  duplicable = true,
  width,
  children,
}: NodeShellProps) {
  const styles = ACCENT[accent]
  const nodeId = useNodeId()
  const { deleteNode, duplicateNode, startMove, moving, issues } = useWorkflowActions()
  const isTrigger = !hasTarget
  // Badge state comes from the one shared issues map, so the card always agrees with the inspector
  // and the toolbar "N to fix" count.
  const issue = nodeId ? issues.get(nodeId) : undefined
  const invalid = !!issue && Object.keys(issue.errors).length > 0
  const warning = !!issue && Object.keys(issue.warnings).length > 0
  const sourceConnections = useNodeConnections({ handleType: `source` })
  const showAppend = hasSource && nodeId != null && sourceConnections.length === 0
  const travelling = !!nodeId && !!moving?.travelling.has(nodeId)
  // Live run status (populated only while watching a run) — the node lights up as the run reaches it.
  const runStatus = useRunStatus()
  const tone = runTone(nodeId ? runStatus.get(nodeId) : undefined)
  const run = tone ? RUN_TONE[tone] : undefined
  return (
    <div
      style={width ? ({ '--card-width': width } as React.CSSProperties) : undefined}
      className={cn(
        `relative w-60 rounded-xl border border-default bg-surface shadow-sm transition-[box-shadow,opacity] hover:shadow-md`,
        width && `w-(--card-width)`,
        travelling && `border-dashed opacity-40`,
        run ? cn(`ring-2 ring-offset-1`, run.ring) : selected && cn(`ring-2 ring-offset-1`, styles.ring),
      )}
    >
      {run && (
        <span
          className={cn(
            `absolute -top-2 -left-1.5 z-10 rounded-full px-1.5 py-0.5 text-[9px] font-semibold shadow-sm`,
            run.chip,
            run.text,
            run.pulse && `animate-pulse`,
          )}
        >
          {run.label}
        </span>
      )}
      <NodeToolbar isVisible={selected && !moving} position={Position.Top} align="end" offset={6}>
        <div className="flex gap-0.5 rounded-lg border border-default bg-surface-raised p-0.5 shadow-md">
          {!isTrigger && (
            <Button
              variant="subtle"
              size="icon-sm"
              onClick={() => nodeId && startMove(nodeId)}
              title="Move"
              aria-label="Move"
            >
              <ArrowsOutCardinalIcon size={15} weight="bold" />
            </Button>
          )}
          {!isTrigger && duplicable && (
            <Button
              variant="subtle"
              size="icon-sm"
              onClick={() => nodeId && duplicateNode(nodeId)}
              title="Duplicate"
              aria-label="Duplicate"
            >
              <CopyIcon size={15} weight="bold" />
            </Button>
          )}
          <Button
            variant="subtle"
            size="icon-sm"
            onClick={() => nodeId && deleteNode(nodeId)}
            title="Delete"
            aria-label="Delete"
            className="text-red-600 hover:text-red-600"
          >
            <TrashIcon size={15} weight="bold" />
          </Button>
        </div>
      </NodeToolbar>
      {invalid ? (
        <span
          title="Needs setup"
          className="absolute -top-1.5 -right-1.5 z-10 flex size-4 items-center justify-center rounded-full bg-amber-500 text-on-color shadow-sm"
        >
          <WarningIcon size={10} weight="bold" />
        </span>
      ) : (
        warning && (
          <span
            title="May not have a contact at run time"
            className="absolute -top-1.5 -right-1.5 z-10 size-3 rounded-full border-2 border-(--surface) bg-amber-400 shadow-sm"
          />
        )
      )}
      {hasTarget && <Handle type="target" position={Position.Top} className={HANDLE_CLASS} />}
      <div className="flex items-start gap-3 p-3">
        <div className={cn(`flex size-8 shrink-0 items-center justify-center rounded-lg`, styles.chip)}>
          <IconCmp size={18} weight="bold" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-semibold tracking-wide text-minimal uppercase">{kind}</div>
          <div className="truncate text-sm font-semibold text-default">{label}</div>
          {description && <div className="mt-0.5 line-clamp-2 text-xs text-subtle">{description}</div>}
        </div>
      </div>
      {children}
      {hasSource && <Handle type="source" position={Position.Bottom} className={HANDLE_CLASS} />}
      {showAppend && <AppendButton nodeId={nodeId} className="-bottom-8 left-1/2 -translate-x-1/2" />}
    </div>
  )
}
